import { t } from 'i18next';
import { ChevronDown, CircleAlert, History, Logs } from 'lucide-react';
import { useMemo } from 'react';
import {
  createSearchParams,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  LeftSideBarType,
  useBuilderStateContext,
} from '@/app/builder/builder-hooks';
import { useEmbedding } from '@/components/embed-provider';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/ui/user-avatar';
import { foldersHooks } from '@/features/folders/lib/folders-hooks';
import { useAuthorization } from '@/hooks/authorization-hooks';
import { flagsHooks } from '@/hooks/flags-hooks';
import { authenticationSession } from '@/lib/authentication-session';
import { cn } from '@/lib/utils';
import {
  FlowStatus,
  FlowType,
  FlowVersionState,
  isNil,
  Permission,
} from '@activepieces/shared';

import FlowActionMenu from '../components/flow-actions-menu';
import { determineDefaultRoute } from '../router/default-route';

import { BuilderPublishButton } from './builder-publish-button';
import { AskAiBuilderButton } from '@/components/ask-ai-builder';
import { flowsApi } from '@/features/flows/lib/flows-api';
import { flowsHooks } from '@/features/flows/lib/flows-hooks';
import { RenameFlowDialog } from '@/features/flows/components/rename-flow-dialog';

const getErrorFlowButtonTooltip = (
  flowType: FlowType | undefined,
  isErrorFlowInactive: boolean,
) => {
  if (flowType === FlowType.ERROR) {
    return t('Open the base flow');
  }
  if (isErrorFlowInactive) {
    return t(
      'This error flow is not activated: no failure notification is sent. Publish and enable it.',
    );
  }
  return t('Open the flow that triggers on an error');
};

export const BuilderHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = flagsHooks.useWebsiteBranding();
  const isInRunsPage = useMemo(
    () => location.pathname.includes('/runs'),
    [location.pathname],
  );
  const hasPermissionToReadRuns = useAuthorization().checkAccess(
    Permission.READ_FLOW,
  );
  const [
    flow,
    flowVersion,
    setLeftSidebar,
    renameFlowClientSide,
    moveToFolderClientSide,
  ] = useBuilderStateContext((state) => [
    state.flow,
    state.flowVersion,
    state.setLeftSidebar,
    state.renameFlowClientSide,
    state.moveToFolderClientSide,
  ]);

  const { embedState } = useEmbedding();

  const { data: folderData } = foldersHooks.useFolder(flow.folderId ?? 'NULL');

  // An error flow is created disabled and unpublished, and nothing else tells the
  // user: an unactivated one silently drops every failure notification (KHB-457).
  const { data: errorFlow } = flowsHooks.useFlow(
    flow.type === FlowType.ERROR ? null : flow.errorFlowId,
  );
  const isErrorFlowInactive =
    !isNil(errorFlow) &&
    (errorFlow.status !== FlowStatus.ENABLED ||
      isNil(errorFlow.publishedVersionId));

  const isLatestVersion =
    flowVersion.state === FlowVersionState.DRAFT ||
    flowVersion.id === flow.publishedVersionId;
  const folderName = folderData?.displayName ?? t('Uncategorized');
  const defaultRoute = determineDefaultRoute(useAuthorization().checkAccess);

  const handleErrorFlowClick = async () => {
    if (!flow.errorFlowId) {
      const newFlow = await flowsApi.createErrorFlow({
        displayName: flowVersion.displayName + `:Failed`,
        // Only a real folder, never the "Uncategorized" placeholder — sending it
        // would create a folder literally named after the translated label.
        folderName: folderData?.displayName,
        baseFlowId: flow.id,
      });
      navigate(`/flows/${newFlow.id}`);
    } else {
      navigate(`/flows/${flow.errorFlowId}`);
    }
  };

  const handleBaseFlowClick = () => {
    if (flow.baseFlowId) {
      navigate(`/flows/${flow.baseFlowId}`);
    }
  };

  return (
    <div className="bg-background select-none">
      <div className="relative items-center flex h-[55px] w-full p-4 bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          {!embedState.hideLogoInBuilder &&
            !embedState.disableNavigationInBuilder && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to={defaultRoute}>
                    <Button variant="ghost" size={'icon'}>
                      <img
                        className="h-5 w-5 object-contain"
                        src={branding.logos.logoIconUrl}
                        alt={branding.websiteName}
                      />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {t('Go to Dashboard')}
                </TooltipContent>
              </Tooltip>
            )}
          <span className='overflow-hidden whitespace-nowrap text-ellipsis'>
            {!embedState.hideFolders && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      onClick={() =>
                        navigate({
                          pathname:
                            authenticationSession.appendProjectRoutePrefix(
                              '/flows',
                            ),
                          search: createSearchParams({
                            folderId: folderData?.id ?? 'NULL',
                          }).toString(),
                        })
                      }
                    >
                      {folderName}
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>
                        {t('Go to folder')} {folderName}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {' / '}
              </>
            )}
            {!embedState.hideFlowNameInBuilder && (
              <RenameFlowDialog
                flowId={flow.id}
                currentFlowName={flowVersion.displayName}
                onRename={(newName) => renameFlowClientSide(newName)}
              >
                <strong className="cursor-pointer">
                  {flowVersion.displayName}
                </strong>
              </RenameFlowDialog>
            )}
          </span>
          <FlowActionMenu
            insideBuilder={true}
            flow={flow}
            flowVersion={flowVersion}
            readonly={!isLatestVersion}
            onDelete={() => {
              navigate('/flows');
            }}
            onRename={(newName) => renameFlowClientSide(newName)}
            onMoveTo={(folderId) => moveToFolderClientSide(folderId)}
            onDuplicate={() => {}}
          >
            <ChevronDown className="h-8 w-8 flex-shrink-0" />
          </FlowActionMenu>
        </div>

        <div className="grow"></div>
        <div className="flex items-center justify-center gap-4">
           <Tooltip>
              <TooltipTrigger asChild>
                <AskAiBuilderButton variant={"basic"} />
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('Support')}</TooltipContent>
            </Tooltip>
          {(
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-2 px-2"
                  onClick={flow.type === FlowType.ERROR ? handleBaseFlowClick : handleErrorFlowClick}
                >
                  <CircleAlert
                    className={cn('w-4 h-4', {
                      'text-destructive': isErrorFlowInactive,
                    })}
                  ></CircleAlert>
                  {flow.type === FlowType.ERROR ? t('Base Flow') : t('Error Flow')}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {getErrorFlowButtonTooltip(flow.type, isErrorFlowInactive)}
              </TooltipContent>
            </Tooltip>
          )}
          {hasPermissionToReadRuns && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={() => setLeftSidebar(LeftSideBarType.RUNS)}
                  className="gap-2 px-2"
                >
                  <Logs className="w-4 h-4" />
                  {t('Runs')}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('Run Logs')}</TooltipContent>
            </Tooltip>
          )}

          {!isInRunsPage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-2 px-2"
                  onClick={() => setLeftSidebar(LeftSideBarType.VERSIONS)}
                >
                  <History className="w-4 h-4" />
                  {t('Versions')}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('Versions History')}
              </TooltipContent>
            </Tooltip>
          )}

          <BuilderPublishButton></BuilderPublishButton>
          <UserAvatar />
        </div>
      </div>
    </div>
  );
};
